import { createMuiTheme, ThemeOptions } from '@material-ui/core'

/**
 * Builder's own layer over the shared v4 theme. The shared one is read rather
 * than changed because admin and client consume it too.
 *
 * MUI ships buttons and tabs in all caps, which makes a row of controls the
 * loudest thing on a page it is rarely the point of. Turning it off lets each
 * label carry the casing it was written in. The tracking goes with it: that
 * extra letter-spacing exists to keep uppercase legible and only looks loose
 * under a sentence-case label.
 *
 * The rest is Material's own look, which builder no longer has anywhere else:
 * a 4px radius against the 8px every Tailwind control on the page uses, and a
 * drop shadow under filled buttons on a flat shell.
 *
 * `createV5Theme` sets the same rules for v5's components — each library styles
 * its own, so neither theme reaches the other's.
 */
export function createV4Theme(base: ThemeOptions): ThemeOptions {
  return createMuiTheme({
    ...base,
    shape: { borderRadius: 8 },
    typography: {
      button: { textTransform: 'none', letterSpacing: 0, fontWeight: 600 },
    },
    props: { MuiButton: { disableElevation: true } },
    overrides: {
      ...base.overrides,
      MuiTab: { root: { textTransform: 'none' } },
      // Column headings shipped as plain body text, so on tables whose rows are
      // mostly chips and buttons the heading row read as one more row of data.
      // Colours are the brand's neutral-2-50 and neutral-1-600; Tailwind's token
      // names aren't reachable from here.
      MuiTableCell: {
        head: {
          backgroundColor: '#F2F4F8',
          color: '#656565',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        },
      },
    },
  })
}
